import { useEffect, useRef, useState } from "react";
import s from "./LocationCard.module.scss";
import ImageWithPlaceholder from "../Image/ImageWithPlaceholder";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_BASE = process.env.REACT_APP_API_LOCAL_URL;

const LocationCarousel = () => {
    const containerRef = useRef();
    const navigate = useNavigate();

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);

    const [itemsPerView, setItemsPerView] = useState(4);
    const [index, setIndex] = useState(0);
    const [isDragging, setIsDragging] = useState(false);

    /*
    ----------------------------
    Load locations from API
    ----------------------------
    */
    useEffect(() => {
        const fetchLocations = async () => {
            try {
                const res = await axios.get(
                    `${API_BASE}locations`
                );

                setLocations(res.data || []);
            } catch (err) {
                console.error("Failed to load locations", err);
            } finally {
                setLoading(false);
            }
        };

        fetchLocations();
    }, []);

    /*
    ----------------------------
    Responsive
    ----------------------------
    */
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 576) {
                setItemsPerView(1);
            } else if (window.innerWidth < 992) {
                setItemsPerView(2);
            } else if (window.innerWidth < 1200) {
                setItemsPerView(3);
            } else {
                setItemsPerView(4);
            }
        };

        handleResize();

        window.addEventListener(
            "resize",
            handleResize
        );

        return () =>
            window.removeEventListener(
                "resize",
                handleResize
            );
    }, []);

    useEffect(() => {
        const total = locations.length;
        if (!total) return;

        if (index >= total) {
            setIndex(0);
        }

        if (index < 0) {
            setIndex(total - 1);
        }
    }, [index, locations]);

    /*
    ----------------------------
    Infinite loop fix
    ----------------------------
    */
    useEffect(() => {
        const total = locations.length;

        if (!total) return;

        if (index === total) {
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.style.transition =
                        "none";

                    setIndex(0);

                    void containerRef.current
                        .offsetHeight;

                    containerRef.current.style.transition =
                        "transform 0.5s ease";
                }
            }, 500);
        }

        if (index === -1) {
            setTimeout(() => {
                if (containerRef.current) {
                    containerRef.current.style.transition =
                        "none";

                    setIndex(total - 1);

                    void containerRef.current
                        .offsetHeight;

                    containerRef.current.style.transition =
                        "transform 0.5s ease";
                }
            }, 500);
        }
    }, [index, locations]);

    /*
    ----------------------------
    Navigation
    ----------------------------
    */
    const maxIndex = Math.max(0, locations.length - itemsPerView);
    const handleNext = () =>
        setIndex(prev => Math.min(prev + 1, maxIndex));

    const handlePrev = () =>
        setIndex(prev => Math.max(prev - 1, 0));

    const handleLocationClick = (
        id,
        name
    ) => {
        const breadcrumbItems = [
            { name: "ホーム", link: "/" },
            {
                name: "ケアンズ以外",
                link: "/categories/location",
            },
            { name, link: "" },
        ];

        navigate(
            `/all-tours?location=${id}`,
            {
                state: { breadcrumbItems },
            }
        );
    };

    /*
    ----------------------------
    Drag
    ----------------------------
    */
    const startX = useRef(0);
    const moved = useRef(false);

    const onStart = (e) => {
        setIsDragging(true);
        moved.current = false;

        startX.current = e.touches
            ? e.touches[0].clientX
            : e.clientX;
    };

    const onMove = (e) => {
        if (!isDragging) return;

        const currentX = e.touches
            ? e.touches[0].clientX
            : e.clientX;

        const diff = Math.abs(
            startX.current - currentX
        );

        if (diff > 5) {
            moved.current = true;
        }
    };

    const onEnd = (e) => {
        if (!isDragging) return;

        setIsDragging(false);

        const endX = e.changedTouches
            ? e.changedTouches[0].clientX
            : e.clientX;

        const diff =
            startX.current - endX;

        if (moved.current) {
            if (diff > 50) handleNext();
            if (diff < -50) handlePrev();
        }
    };

    /*
    ----------------------------
    Loading
    ----------------------------
    */
    if (loading) return null;

    if (!locations.length) return null;

    /*
    ----------------------------
    Clone for infinite loop
    ----------------------------
    */
    const extended = locations;

    return (
        <div className={s.carousel}>
            <div
                className={s.inner}
                ref={containerRef}
                style={{
                    transform: `translateX(-${
                        index *
                        (100 /
                            itemsPerView)
                    }%)`,
                    transition:
                        isDragging
                            ? "none"
                            : "transform 0.6s cubic-bezier(0.25,0.46,0.45,0.94)",
                }}
                onMouseDown={onStart}
                onMouseMove={onMove}
                onMouseUp={onEnd}
                onTouchStart={onStart}
                onTouchMove={onMove}
                onTouchEnd={onEnd}
            >
                {extended.map(
                    (item, i) => (
                        <div
                            key={item.id}
                            className={
                                s.item
                            }
                            style={{
                                flex: `0 0 ${
                                    100 /
                                    itemsPerView
                                }%`,
                            }}
                            onClick={() =>
                                handleLocationClick(
                                    item.id,
                                    item.name
                                )
                            }
                        >
                            <div
                                className={
                                    s.card
                                }
                            >
                                <ImageWithPlaceholder
                                    src={
                                        item.image
                                    }
                                    alt={
                                        item.name
                                    }
                                />

                                <div
                                    className={
                                        s.overlay
                                    }
                                >
                                    {
                                        item.name
                                    }
                                </div>
                            </div>
                        </div>
                    )
                )}
            </div>

            <button
                className={s.prev}
                onClick={handlePrev}
            >
                ‹
            </button>

            <button
                className={s.next}
                onClick={handleNext}
            >
                ›
            </button>
        </div>
    );
};

export default LocationCarousel;